import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dryRunAutomation, getAutomations, getEngagementSettings, patchAutomation } from 'API/engagement';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AutomationDryRun, EngagementAutomation } from 'types/engagement.types';
import { fmtDate } from '../../shared/format';
import { QueryState, SectionCard } from '../../shared/ui';
import { deriveAutomationsEnabled, normalizeAutomations } from '../switches';
import { SendDisabledBanner } from './messages-tab';

const triggerText = (a: EngagementAutomation): string =>
  typeof a.trigger === 'string' ? a.trigger : a.trigger ? JSON.stringify(a.trigger) : '—';

export const AutomationsTable: React.FC<{
  automations: EngagementAutomation[];
  automationsEnabled?: boolean;
  onToggle(key: string, enabled: boolean): void;
  onDryRun(key: string): void;
  busyKey?: string | null;
}> = ({ automations, automationsEnabled, onToggle, onDryRun, busyKey }) => {
  const { t, i18n } = useTranslation();
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size={'small'}>
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>{t('engColAutomation')}</TableCell>
            <TableCell>{t('engColTrigger')}</TableCell>
            <TableCell>{t('engColChannels')}</TableCell>
            <TableCell>{t('engColLastRun')}</TableCell>
            <TableCell align={'right'}>{t('engColEvaluated')}</TableCell>
            <TableCell align={'right'}>{automationsEnabled === false ? t('engColWouldSend') : t('engColSent')}</TableCell>
            <TableCell align={'right'}>{t('engColSkipped')}</TableCell>
            <TableCell>{t('engColEnabled')}</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {[...automations]
            .sort((a, b) => a.priority - b.priority)
            .map((a) => {
              const s = a.lastRunStats;
              return (
                <TableRow key={a.key}>
                  <TableCell sx={{ fontWeight: 600 }}>{a.key}</TableCell>
                  <TableCell>
                    {a.name}
                    {a.killSwitch ? <Chip size={'small'} color={'error'} label={t('engKillSwitchShort')} sx={{ ml: 1 }} title={a.killSwitch.reason} /> : null}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 260 }}>
                    <Typography sx={{ fontSize: 12 }}>{triggerText(a)}</Typography>
                  </TableCell>
                  <TableCell>{(a.channels ?? []).map((c) => t(`engChannel_${c}`)).join(', ') || '—'}</TableCell>
                  <TableCell>{a.lastRunAt ? fmtDate(a.lastRunAt, i18n.language, true) : t('engNeverRun')}</TableCell>
                  <TableCell align={'right'}>{s ? s.evaluated : '—'}</TableCell>
                  <TableCell align={'right'} data-testid={`automation-sent-${a.key}`}>
                    {s ? (automationsEnabled === false ? t('engWouldSendValue', { count: s.wouldSend }) : `${s.sent}${s.wouldSend > s.sent ? ` / ${s.wouldSend}` : ''}`) : '—'}
                  </TableCell>
                  <TableCell align={'right'}>{s ? s.skipped : '—'}</TableCell>
                  <TableCell>
                    <Switch size={'small'} checked={a.enabled} disabled={busyKey === a.key} onChange={(e) => onToggle(a.key, e.target.checked)} />
                  </TableCell>
                  <TableCell>
                    <Button size={'small'} onClick={() => onDryRun(a.key)}>
                      {t('engDryRun')}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
        </TableBody>
      </Table>
    </Box>
  );
};

const DryRunDialog: React.FC<{ automationKey: string | null; result?: AutomationDryRun; loading: boolean; error: unknown; onClose(): void }> = ({
  automationKey,
  result,
  loading,
  error,
  onClose,
}) => {
  const { t } = useTranslation();
  const rows = result?.audience ?? [];
  // Candidates also include already-sent, lower-priority and control-group users; only would_send goes out.
  const count = result?.counts?.would_send ?? 0;
  return (
    <Dialog open={!!automationKey} onClose={onClose} maxWidth={'sm'} fullWidth>
      <DialogTitle>{t('engDryRunTitle', { key: automationKey ?? '' })}</DialogTitle>
      <DialogContent>
        <QueryState isLoading={loading} error={error}>
          <Typography sx={{ mb: 1.5 }}>{t('engDryRunCount', { count })}</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>{t('engDryRunNote')}</Typography>
          {rows.length ? (
            <Table size={'small'}>
              <TableBody>
                {rows.slice(0, 100).map((r, i) => (
                  <TableRow key={`${r.userId ?? r.tenantId}-${i}`}>
                    <TableCell>{r.userId ?? '—'}</TableCell>
                    <TableCell>{r.tenantId ?? ''}</TableCell>
                    <TableCell>
                      {t(`engDryRunStatus_${r.status}`, { defaultValue: r.status })}
                      {r.templateKey ? ` · ${r.templateKey}` : ''}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
        </QueryState>
      </DialogContent>
    </Dialog>
  );
};

const AutomationsTab: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const automations = useQuery({ queryKey: ['engagement', 'automations'], queryFn: getAutomations, select: normalizeAutomations });
  const settings = useQuery({ queryKey: ['engagement', 'settings'], queryFn: getEngagementSettings });
  const [dryKey, setDryKey] = useState<string | null>(null);

  const toggle = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) => patchAutomation(key, { enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['engagement', 'automations'] }),
  });
  const dryRun = useMutation({ mutationFn: dryRunAutomation });

  const data = automations.data;
  const enabled = data ? deriveAutomationsEnabled(data, settings.data) : undefined;
  const explicit = typeof data?.automationsEnabled === 'boolean' || typeof settings.data?.flags?.automationsEnabled === 'boolean';

  return (
    <Stack gap={2}>
      {enabled === false ? <SendDisabledBanner kind={'automations'} derived={!explicit} /> : null}
      <SectionCard title={t('engAutomationsTitle')}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1.5 }}>{t('engAutomationsIntro')}</Typography>
        {toggle.error ? (
          <Alert severity={'error'} sx={{ mb: 1 }}>
            {(toggle.error as Error).message}
          </Alert>
        ) : null}
        <QueryState isLoading={automations.isLoading} error={automations.error} isEmpty={(data?.automations ?? []).length === 0} emptyText={t('engAutomationsEmpty')}>
          <AutomationsTable
            automations={data?.automations ?? []}
            automationsEnabled={enabled}
            busyKey={toggle.isPending ? toggle.variables?.key : null}
            onToggle={(key, value) => toggle.mutate({ key, enabled: value })}
            onDryRun={(key) => {
              setDryKey(key);
              dryRun.mutate(key);
            }}
          />
        </QueryState>
      </SectionCard>
      <DryRunDialog automationKey={dryKey} result={dryRun.data} loading={dryRun.isPending} error={dryRun.error} onClose={() => setDryKey(null)} />
    </Stack>
  );
};

export default AutomationsTab;
